import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shuffle, CalendarDays, Bookmark, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Reusable Daily Haiku card. variant="hero" is compact for the landing page.
export default function DailyHaikuCard({ variant = "full" }) {
  const { user } = useAuth();
  const [entry, setEntry] = useState(null); // { haiku, date?, total }
  const [isRandom, setIsRandom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedIndex, setSavedIndex] = useState(null);

  const loadDaily = useCallback(() => {
    setLoading(true);
    api.get("/daily-haiku")
      .then((res) => { setEntry(res.data); setIsRandom(false); })
      .catch(() => setEntry(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadDaily(); }, [loadDaily]);

  const showAnother = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/daily-haiku/random");
      setEntry((prev) => ({ ...data, date: prev?.date }));
      setIsRandom(true);
    } catch (e) {
      toast.error("Could not draw another verse. Please try again.");
    }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!entry?.haiku) return;
    setSaving(true);
    try {
      const s = entry.haiku.season || "";
      const season = s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "Spring";
      await api.post("/hokku", { text: entry.haiku.lines.join("\n"), season });
      setSavedIndex(entry.haiku.index);
      toast.success("Saved to your collection", { description: "Find it under My Verses → Your Hokku collection." });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground font-serif text-xl" data-testid="daily-haiku-loading">Drawing today's verse…</div>;
  }
  if (!entry?.haiku) {
    return <div className="text-muted-foreground" data-testid="daily-haiku-error">The daily verse is resting. Please try again shortly.</div>;
  }

  const h = entry.haiku;
  const hero = variant === "hero";

  return (
    <div data-testid="daily-haiku-card" className="w-full">
      <div className="flex items-center gap-2 mb-5">
        <span className="hanko text-primary text-lg">今日</span>
        <span className="text-xs tracking-[0.25em] uppercase text-secondary">
          {isRandom ? "A verse from the collection" : "Haiku of the day"}
        </span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={h.index}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <p
            data-testid="daily-haiku-text"
            className={`font-poem leading-relaxed text-foreground ${hero ? "text-2xl sm:text-3xl md:text-4xl" : "text-3xl sm:text-4xl md:text-5xl"}`}
          >
            {h.lines[0]}<br />{h.lines[1]}<br />{h.lines[2]}
          </p>

          <p className="mt-6 text-sm text-secondary">— Matsuo Bashō</p>

          {(h.season || h.chapter || h.tags?.length > 0) && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {h.season && (
                <span className="text-[11px] tracking-wide px-2.5 py-1 border border-secondary/40 text-secondary" data-testid="daily-haiku-season">
                  季 · {h.season}
                </span>
              )}
              {h.tags?.map((t) => (
                <span key={t} className="text-[11px] tracking-wide px-2.5 py-1 border border-border text-muted-foreground">{t}</span>
              ))}
            </div>
          )}

          {h.note && (
            <p className="mt-5 text-sm text-muted-foreground italic border-l border-border pl-4 max-w-xl" data-testid="daily-haiku-note">
              {h.note}
            </p>
          )}
          {!hero && h.chapter && (
            <p className="mt-4 text-xs text-muted-foreground/80">{h.chapter}</p>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button
          onClick={showAnother}
          disabled={busy}
          data-testid="daily-haiku-another"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <Shuffle className="w-4 h-4" /> {busy ? "Drawing…" : "Show another"}
        </button>
        {user && (
          savedIndex === h.index ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-secondary" data-testid="daily-haiku-saved">
              <Check className="w-4 h-4" /> Saved
            </span>
          ) : (
            <button
              onClick={save}
              disabled={saving}
              data-testid="daily-haiku-save"
              className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            >
              <Bookmark className="w-4 h-4" /> {saving ? "Saving…" : "Save to My Verses"}
            </button>
          )
        )}
        {isRandom && (
          <button
            onClick={loadDaily}
            data-testid="daily-haiku-back"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <CalendarDays className="w-4 h-4" /> Back to today's
          </button>
        )}
        {entry.total && (
          <span className="text-xs text-muted-foreground">{entry.total} verses in the collection</span>
        )}
      </div>
    </div>
  );
}
